'use client';

import { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { subscribeUser, unsubscribeUser } from '@/lib/actions/notifications';

function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export function PushManager() {
    const [isSupported, setIsSupported] = useState(false);
    const [subscription, setSubscription] = useState<PushSubscription | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            setIsSupported(true);
            registerServiceWorker();
        }
    }, []);

    async function registerServiceWorker() {
        try {
            const registration = await navigator.serviceWorker.register('/sw.js', {
                scope: '/',
                updateViaCache: 'none',
            });
            const sub = await registration.pushManager.getSubscription();
            setSubscription(sub);
        } catch (error) {
            console.error('Service Worker registration failed:', error);
        }
    }

    async function subscribeToPush() {
        setLoading(true);
        try {
            const registration = await navigator.serviceWorker.ready;
            const sub = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(
                    process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY!
                ),
            });

            setSubscription(sub);
            // Cast to any to avoid strict type mismatch with server action
            await subscribeUser(sub.toJSON() as any);
            alert('Vous êtes abonné aux notifications ! 🔔');
        } catch (error) {
            console.error('Subscription failed:', error);
            alert('Impossible de vous abonner. Vérifiez vos permissions navigateur.');
        }
        setLoading(false);
    }

    async function unsubscribeFromPush() {
        setLoading(true);
        try {
            if (subscription) {
                await subscription.unsubscribe();
                await unsubscribeUser(subscription.endpoint);
                setSubscription(null);
                alert('Abonnement supprimé.');
            }
        } catch (error) {
            console.error('Unsubscription failed:', error);
        }
        setLoading(false);
    }

    if (!isSupported) return null;

    return (
        <button
            onClick={subscription ? unsubscribeFromPush : subscribeToPush}
            disabled={loading}
            className={`fixed bottom-6 left-6 z-40 p-3 rounded-full shadow-lg transition-all duration-300 ${subscription
                ? 'bg-primary text-secondary hover:bg-yellow-400'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
            title={subscription ? "Désactiver les notifications" : "Activer les notifications"}
        >
            {loading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-inherit"></div>
            ) : (
                subscription ? <BellRing size={24} /> : <Bell size={24} />
            )}
        </button>
    );
}
